import React, {useEffect, useMemo, useState, useRef} from 'react'
import {dataAniversarioFields, mapDataAniversarioToTable} from "./data-aniversario-fields"
import {Paper} from "@mui/material"
import DynamicColumnTable from "../../../componentes/dynamic-column-table"
import PropTypes from "prop-types"
import {copyTable, pasteTableFromClipboard} from "../../../../utils/csv-utils"
import MotorDataAniversarioService from "../../../../../../../services/motor-data-aniversario-service"
import {useLocation} from "react-router-dom"
import {useDraftLoadGuard} from "../../../use-draft-load-guard"
import {dispatchSnackbar} from "../../../../../../components/SnackBar/snackbar-bus"

const DataAniversarioTable = ({
    setLoading,
    idCaracteristica,
    dataAniversarioPublicado,
    showDataAniversario,
    tableData,
    setTableData,
    clearTable,
    datasAniversarioProjetadas,
    onDraftEdit,
}) => {

    const service = useMemo(() => new MotorDataAniversarioService(), [])
    const [tableKey, setTableKey] = useState(Math.random() * 10)
    const location = useLocation()
    const navigationState = location.state
    const {beginDraftLoad, onLocalEdit} = useDraftLoadGuard(idCaracteristica, tableData, onDraftEdit)

    const possuiProjecaoRef = useRef(
        datasAniversarioProjetadas !== null
    )

    possuiProjecaoRef.current =
        datasAniversarioProjetadas !== null

    useEffect(() => {
        if (datasAniversarioProjetadas === null) return

        const formatted = mapDataAniversarioToTable(
            datasAniversarioProjetadas
        )

        setTableData(formatted)
        setTableKey(Math.random() * 10)
    }, [datasAniversarioProjetadas, setTableData])

    useEffect(() => {
        if (!showDataAniversario) return
        if (!idCaracteristica) return

        const origem = navigationState?.origem

        if (origem === "ver") {
            loadDataAniversario(beginDraftLoad())
        }
    }, [
        beginDraftLoad,
        showDataAniversario,
        idCaracteristica,
        navigationState,
    ])

    useEffect(() => {
        if (navigationState?.origem !== "duplicar") return

        const dadosOrigem =
            navigationState?.Data?.DataAniversario ??
            navigationState?.Data?.DatasAniversario ??
            []

        const formatted =
            mapDataAniversarioToTable(dadosOrigem)

        dataAniversarioPublicado(false)

        setTableData(formatted)
        setTableKey(Math.random() * 10)
    }, [
        navigationState,
        setTableData,
        dataAniversarioPublicado,
    ])

    const aplicarDadosDoGet = (dados, guard) => {
        if (!guard.canApply() || possuiProjecaoRef.current) return

        const formatted = mapDataAniversarioToTable(dados)

        setTableData(prev => {
            if (!guard.canApply() || possuiProjecaoRef.current) {
                return prev
            }

            return formatted
        })

        setTableKey(Math.random() * 10)
    }

    const loadDataAniversario = async (guard) => {
        let failed = false
        setLoading(true)
        try {
            const response = await service.getDataAniversario(idCaracteristica)
            if (!guard.canApply() || possuiProjecaoRef.current) return

            const {body} = response
            if (!Array.isArray(body)) throw new Error("Resposta de datas de aniversário inválida")

            if (body.length > 0) {
                dataAniversarioPublicado(true)
                aplicarDadosDoGet(body, guard)
            } else {
                await loadDataAniversarioTemporario(guard)
            }
        } catch {
            if (guard.canApply()) {
                failed = true
                dispatchSnackbar("Não foi possível carregar as datas de aniversário.", "error")
            }
        } finally {
            if (guard.isCurrent()) setLoading(failed ? "error" : false)
        }
    }

    const loadDataAniversarioTemporario = async (guard) => {
        try {
            const response = await service.getTemporarioDataAniversario(idCaracteristica)
            if (!guard.canApply() || possuiProjecaoRef.current) return

            const bodyTemp = response?.body || response?.data

            if (!bodyTemp) {
                throw new Error("Resposta de datas de aniversário inválida")
            }

            const aniversarios = Array.isArray(bodyTemp)
                ? bodyTemp
                : bodyTemp?.DatasAniversario
            if (!Array.isArray(aniversarios)) throw new Error("Resposta de datas de aniversário inválida")


            aplicarDadosDoGet(aniversarios, guard)
        } catch (error) {
            if (error?.status !== 400) throw error
        }
    }

    return (
        <Paper sx={{mt: 2}}>
            <DynamicColumnTable
                key={tableKey}
                actionButtons={true}
                copyData={() => copyTable(tableData, dataAniversarioFields)}
                pasteData={async () => await pasteTableFromClipboard(
                    dataAniversarioFields,
                    (rows) => {
                        onLocalEdit()
                        setTableData(rows)
                    },
                )}
                clearTable={() => {
                    onLocalEdit()
                    clearTable(setTableData)
                }}
                onUserEdit={onLocalEdit}
                fields={dataAniversarioFields}
                items={tableData}
                setItems={setTableData}
            />
        </Paper>
    )
}

DataAniversarioTable.propTypes = {
    setLoading: PropTypes.func,
    idCaracteristica: PropTypes.number,
    dataAniversarioPublicado: PropTypes.func,
    showDataAniversario: PropTypes.bool,
    tableData: PropTypes.array,
    setTableData: PropTypes.func,
    clearTable: PropTypes.func,
    datasAniversarioProjetadas: PropTypes.array,
    onDraftEdit: PropTypes.func,
}

export default DataAniversarioTable
